'use memo';

import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
} from '@urbanx/agx.ui-components2';

interface TaskProgressorConfirmationModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isPending: boolean;
  campaignLabel: string;
  contractLabel?: string;
  modeLabel: string;
  targetStageLabel?: string;
  currentCampaignStageLabel?: string;
  resultingCampaignStageLabel?: string;
  currentContractStageLabel?: string;
  resultingContractStageLabel?: string;
  onConfirm: () => void;
}

const TaskProgressorConfirmationModal = ({
  isOpen,
  setIsOpen,
  isPending,
  campaignLabel,
  contractLabel,
  modeLabel,
  targetStageLabel,
  currentCampaignStageLabel,
  resultingCampaignStageLabel,
  currentContractStageLabel,
  resultingContractStageLabel,
  onConfirm,
}: TaskProgressorConfirmationModalProps) => (
  <AgxModal
    id="task-progressor-confirm-modal"
    title="Replace workflow tasks"
    titleIcon={
      <Icon_FeaturedIcon
        icon={<Icon_AlertCircleOutline type="Danger" />}
        color="var(--text-error)"
        backgroundColor="var(--bg-error-light)"
      />
    }
    type="single"
    variant="withDivider"
    actionButtonContainerStyle="withDivider"
    isOpen={isOpen}
    setIsDialogOpen={setIsOpen}
    actionButtons={
      <AgxSpacer direction="horizontal" gap="spacing-md" contentJustification="center" fill>
        <AgxButton
          id="task-progressor-confirm-cancel"
          text="Cancel"
          variant="hollow"
          stretch
          disabled={isPending}
          onClick={() => setIsOpen(false)}
        />
        <AgxButton
          id="task-progressor-confirm-replace"
          text={isPending ? 'Replacing' : 'Replace tasks'}
          variant="primary"
          stretch
          disabled={isPending}
          onClick={onConfirm}
        />
      </AgxSpacer>
    }
    content={
      <AgxSpacer direction="vertical" gap="spacing-lg" padding="spacing-4xl">
        <AgxTypography id="task-progressor-confirm-warning" type="body" size="medium">
          This permanently deletes every existing task, assignment, and completed step
          <br />
          for the selected {contractLabel ? 'contract' : 'campaign'} workflow before creating the
          canonical task set.
        </AgxTypography>
        <AgxTypography id="task-progressor-confirm-autopilot-only" type="body" size="medium">
          This changes the {contractLabel ? 'contract' : 'campaign'} workflow in Autopilot only.
          CRM, REX, and other downstream systems will not be notified.
        </AgxTypography>
        <AgxTypography id="task-progressor-confirm-campaign" type="body" size="small">
          Campaign: {campaignLabel}
        </AgxTypography>
        {contractLabel && (
          <AgxTypography id="task-progressor-confirm-contract" type="body" size="small">
            Contract: {contractLabel}
          </AgxTypography>
        )}
        {currentCampaignStageLabel && (
          <AgxTypography id="task-progressor-confirm-campaign-stage" type="body" size="small">
            Campaign stage: {currentCampaignStageLabel} → {resultingCampaignStageLabel ?? '-'}
          </AgxTypography>
        )}
        {currentContractStageLabel && (
          <AgxTypography id="task-progressor-confirm-contract-stage" type="body" size="small">
            Contract stage: {currentContractStageLabel} → {resultingContractStageLabel ?? '-'}
          </AgxTypography>
        )}
        <AgxTypography id="task-progressor-confirm-mode" type="body" size="small">
          Mode: {modeLabel}
        </AgxTypography>
        {targetStageLabel && (
          <AgxTypography id="task-progressor-confirm-stage" type="body" size="small">
            Workflow stage to enter: {targetStageLabel}
          </AgxTypography>
        )}
      </AgxSpacer>
    }
  />
);

export default TaskProgressorConfirmationModal;
